import { FC, useEffect, useState } from 'react';
import { useLazyGetAllCustomersQuery } from 'containers/invoices/api';
import DropdownWithSearch from 'components/dropdown-with-search/DropdownWithSearch';
import { joinTextArray } from 'utils/commonUtils';
import { pluckValuesFromArrayWithSpecificKey } from 'utils/formatters';
import CustomDateRangePicker from 'components/react-date-range-picker/ReactDateRangePicker';
import { TipInvoiceFilter } from 'containers/home/components/constants';
import { TypeFilters } from '../constants';
import { ReceiptStatus } from '../receipts.model';
import { useLazyGetMerchantsByTeamIdReceiptsQuery } from '../api';
import { getPaymentMethodFilters } from 'containers/invoices/utils';
import { getReceiptStatusFilter } from '../util';

interface ReceiptFilterProps {
  currentTeamId: number;
  className?: string;
  selectedCustomers: string[];
  setSelectedCustomers: (value: string[]) => void;
  selectedMerchants: string[];
  setSelectedMerchants: (value: string[]) => void;
  paymentMethods: string[];
  setPaymentMethods: (value: string[]) => void;
  startDate: string;
  endDate: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  tipIncluded: string[];
  setTipIncluded: (value: string[]) => void;
  types: string[];
  setTypes: (value: string[]) => void;
  statuses: string[];
  setStatuses: (value: ReceiptStatus[]) => void;
}

const ReceiptFilters: FC<ReceiptFilterProps> = ({
  currentTeamId,
  className,
  selectedCustomers,
  setSelectedCustomers,
  selectedMerchants,
  paymentMethods,
  setSelectedMerchants,
  setPaymentMethods,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  tipIncluded,
  setTipIncluded,
  types,
  setTypes,
  statuses,
  setStatuses
}) => {
  const [getCustomersQuery, getCustomersQueryState] = useLazyGetAllCustomersQuery();
  const [getMerchantsQuery, getMerchantsQueryState] = useLazyGetMerchantsByTeamIdReceiptsQuery();

  const [customerSearchString, setCustomerSearchString] = useState('');
  const [selectedCustomersDraft, setSelectedCustomersDraft] = useState<string[]>([]);
  const [tipIncludedDraft, setTipIncludedDraft] = useState<string[]>([]);
  const [paymentMethodsDraft, setPaymentMethodsDraft] = useState<string[]>([]);
  const [merchantSearchString, setMerchantSearchString] = useState('');
  const [selectedMerchantsDraft, setSelectedMerchantsDraft] = useState<string[]>([]);
  const [statusesDraft, setStatusesDraft] = useState<string[]>([]);
  const [typesDraft, setTypesDraft] = useState<string[]>([]);

  const paymentMethodFilters = getPaymentMethodFilters() || [];
  const receiptStatusFilters = getReceiptStatusFilter() || [];

  useEffect(() => {
    if (currentTeamId) {
      getCustomersQuery({ teamId: currentTeamId });
      getMerchantsQuery({ teamId: currentTeamId });
    }
  }, [currentTeamId]);

  const getTextForFilter = (filter: string) => {
    switch (filter) {
      case 'customers':
        return joinTextArray(
          pluckValuesFromArrayWithSpecificKey(
            getCustomersResponse?.customers?.filter(item => selectedCustomers.includes(item['ID'])),
            'name',
            'lastName'
          )
        );
      case 'merchants':
        return joinTextArray(
          pluckValuesFromArrayWithSpecificKey(
            getMerchantsResponse?.filter(item => selectedMerchants.includes(item['merchantId'])),
            'firstName',
            'lastName'
          )
        );
      case 'statuses':
        return joinTextArray(
          pluckValuesFromArrayWithSpecificKey(
            receiptStatusFilters.filter(item => statuses.includes(item.key)),
            'value'
          )
        );
      case 'paymentMethods':
        return joinTextArray(
          pluckValuesFromArrayWithSpecificKey(
            paymentMethodFilters.filter(item => paymentMethods.includes(item.key)),
            'value'
          )
        );
      case 'tipIncluded':
        return joinTextArray(
          pluckValuesFromArrayWithSpecificKey(
            TipInvoiceFilter.filter(item => tipIncluded.includes(item.key)),
            'value'
          )
        );
      case 'types':
        return joinTextArray(
          pluckValuesFromArrayWithSpecificKey(
            TypeFilters.filter(item => types.includes(item.key)),
            'value'
          )
        );
    }
    return '';
  };

  const { data: getCustomersResponse } = getCustomersQueryState;
  const { data: getMerchantsResponse } = getMerchantsQueryState;

  return (
    <div className={`flex flex-row items-center justify-between ${className}`}>
      <div className="flex flex-wrap">
        <div className="mb-3 pr-3">
          <DropdownWithSearch
            searchFilterId="receipts-customer-name-search-field"
            filterId="receipts-customer-filter"
            data={getCustomersResponse?.customers || []}
            searchText={customerSearchString}
            setSearchText={setCustomerSearchString}
            selectedDraftArray={selectedCustomersDraft}
            setSelectedDraftArray={setSelectedCustomersDraft}
            selectedArray={selectedCustomers}
            setSelectedArray={setSelectedCustomers}
            dataKey="name"
            secondaryDataKey="lastName"
            dataIdKey={'ID'}
            buttonPrimaryText="Customers"
            buttonSecondaryText={getTextForFilter('customers')}
            filterPlaceholderText="Filter by customers"
            searchPlaceHolderText="Search customer names"
            allowSearch={true}
          />
        </div>
        <div className="mb-3 pr-3">
          <DropdownWithSearch
            searchFilterId="receipts-merchant-name-search-field"
            filterId="receipts-createdby-filter"
            data={getMerchantsResponse || []}
            searchText={merchantSearchString}
            setSearchText={setMerchantSearchString}
            selectedDraftArray={selectedMerchantsDraft}
            setSelectedDraftArray={setSelectedMerchantsDraft}
            selectedArray={selectedMerchants}
            setSelectedArray={setSelectedMerchants}
            dataKey="firstName"
            secondaryDataKey="lastName"
            dataIdKey={'merchantId'}
            buttonPrimaryText="Created by"
            buttonSecondaryText={getTextForFilter('merchants')}
            filterPlaceholderText="Filter by merchants"
            searchPlaceHolderText="Search merchant names"
            allowSearch={true}
          />
        </div>
        <div className="mb-3 pr-3">
          <CustomDateRangePicker
            filterId="receipts-date-filter"
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </div>
        <div className="mb-3 pr-3">
          <DropdownWithSearch
            filterId="receipts-status-filter"
            data={receiptStatusFilters}
            selectedDraftArray={statusesDraft}
            setSelectedDraftArray={setStatusesDraft}
            selectedArray={statuses}
            setSelectedArray={setStatuses}
            dataKey="value"
            dataIdKey="key"
            buttonPrimaryText="Status"
            buttonSecondaryText={getTextForFilter('statuses')}
            filterPlaceholderText="Filter by status"
            allowSearch={false}
          />
        </div>
        <div className="mb-3 pr-3">
          <DropdownWithSearch
            filterId="receipts-payment-method-filter"
            data={paymentMethodFilters}
            selectedDraftArray={paymentMethodsDraft}
            setSelectedDraftArray={setPaymentMethodsDraft}
            selectedArray={paymentMethods}
            setSelectedArray={setPaymentMethods}
            dataKey="value"
            dataIdKey="key"
            buttonPrimaryText="Payment method"
            buttonSecondaryText={getTextForFilter('paymentMethods')}
            filterPlaceholderText="Filter by payment method"
            allowSearch={false}
          />
        </div>
        <div className="mb-3 pr-3">
          <DropdownWithSearch
            filterId="receipts-type-filter"
            data={TypeFilters}
            selectedDraftArray={typesDraft}
            setSelectedDraftArray={setTypesDraft}
            selectedArray={types}
            setSelectedArray={setTypes}
            dataKey="value"
            dataIdKey="key"
            buttonPrimaryText="Type"
            buttonSecondaryText={getTextForFilter('types')}
            filterPlaceholderText="Filter by type"
            allowSearch={false}
          />
        </div>
        <div className="mb-3 pr-3">
          <DropdownWithSearch
            filterId="receipts-tip-collected-filter"
            data={TipInvoiceFilter}
            selectedDraftArray={tipIncludedDraft}
            setSelectedDraftArray={setTipIncludedDraft}
            selectedArray={tipIncluded}
            setSelectedArray={setTipIncluded}
            dataKey="value"
            dataIdKey="key"
            buttonPrimaryText="Tip collected"
            buttonSecondaryText={getTextForFilter('tipIncluded')}
            filterPlaceholderText="Filter by tip collected"
            allowSearch={false}
            showResultsCount={false}
          />
        </div>
      </div>
    </div>
  );
};

export default ReceiptFilters;
